import type { SUBJECT_6W_URI } from '~/features-shared/libs/interfaces/entity'
import { logger } from '~/features-shared/libs/utils/logger'
import type { ITag, ITagResponse, ITaggable, ITaggableOpts } from '~/features-shared/tags/interfaces/tags'
import { extractTagsFromExaleadResponse } from '~/features-shared/tags/utils/6wpanel'

export function useTaggable({
  tagsFetchDelay = 0,
  fetchTagsSeparatelyOnRefresh = true,
}: ITaggableOpts): ITaggable {
  // State
  const sixWTags = reactive<ITag[]>([])
  const shouldFetchTags = ref(fetchTagsSeparatelyOnRefresh)
  const tagsFetchedAtleastOnce = ref(false)

  // State that is exposed
  const tags = computed(() => sixWTags)
  const tagsWithSubjectURIData = computed(() => {
    const obj: Record<SUBJECT_6W_URI, ITag[]> = {}
    sixWTags.filter(tag => Object.hasOwnProperty.call(tag, 'subjectURI'))
      .forEach((tag) => {
        const subject6wuri = tag.subjectURI as SUBJECT_6W_URI
        if (!(subject6wuri in obj))
          obj[subject6wuri] = []

        obj[subject6wuri].push(tag)
      })
    return reactive(obj)
  })
  const tagsWithoutSubjectURIData = computed(() =>
    reactive(sixWTags.filter(tag => !Object.hasOwnProperty.call(tag, 'subjectURI'))))

  /**
   * Takes the response of the exalead API as input and pushes the extracted tags
   * @param rawTags Response from API
   */
  const setRawTags = (rawTags: ITagResponse | null) => {
    // Remove existing tags
    sixWTags.length = 0
    if (!rawTags)
      return

    // The response may already have tags. Use them
    const processedTags = rawTags.tags || extractTagsFromExaleadResponse(rawTags.groups)
    sixWTags.push(...processedTags)
  }

  /**
   * Set this value to not fetch the tags after the first time
   */
  const postTagsFetch = () => {
    tagsFetchedAtleastOnce.value = true
  }

  /**
   * Fetches that tags and saves them. Should be called whenever we have to retreive tags from the server
   *
   * @param fetchTags Callback to fetch the tags
   */
  const fetchTagsSeparately = async (fetchTags: () => Promise<unknown>) => {
    // Return if we already fetched some tags
    if (!shouldFetchTags.value)
      return

    if (tagsFetchDelay > 0 && tagsFetchedAtleastOnce.value === false) {
      // Execute with delay if delay is defined
      setTimeout(async () => {
        postTagsFetch()
        try {
          const data = await fetchTags()
          setRawTags(data as ITagResponse)
        }
        catch (err) {
          logger.error(err)
          // Remove tags if there was an error
          setRawTags(null)
        }
      }, tagsFetchDelay)
      return
    }

    try {
      // Just call the callback and set tags if no delay is defined
      setRawTags((await fetchTags()) as ITagResponse)
    }
    catch (err) {
      logger.error(err)
      setRawTags(null)
    }
    postTagsFetch()
  }

  return {
    enableTagging: false,
    temporaryTags: [],

    tags,
    tagsWithSubjectURIData,
    tagsWithoutSubjectURIData,
    shouldFetchTags,

    setRawTags,
    postTagsFetch,
    fetchTagsSeparately,
  }
}
